/**
 * SpreadRenderer3D.js
 *
 * Replaces the 2D Canvas distortion with a true WebGL photorealistic render.
 * Uses Three.js to map page canvases onto mathematically curved meshes,
 * providing accurate physical lighting, glossy specular highlights, and
 * raytraced contact shadows.
 */

import * as THREE from 'three';

const SHEET = 0.002; // Scene units per leaf (page width = 3 units)

const DEFAULTS = {
  width: 1200,
  height: 800,
  binding: 'saddle',   // 'saddle' | 'perfect'
  pages: 60,           // Page count — drives spine height & stiffness
  curveIntensity: 0.15,// User edge-lift override (0 = flat, 0.5 = fanned)
  cameraTiltX: 45,      // Camera looking down (degrees)
  cameraRotateY: 0,     // Rotating around the magazine (degrees)
  cameraRotateZ: 0,     // Spinning flat on the table (degrees)
  lightIntensity: 1.5,
  ambientIntensity: 0.6,
  shadowStrength: 0.25, // Ground contact shadow opacity (0 = none)
  // Image framing within each page
  aspectRatio: null,    // null = derive from source page; else page h/w ratio
  zoom: 1,              // >1 zooms into the page image (crops)
  panX: 0,              // -1..1 horizontal reposition within crop slack
  panY: 0,              // -1..1 vertical reposition within crop slack
  roughness: 0.3,       // 0 = glossy, 1 = matte paper finish
};

export class SpreadRenderer3D {
  constructor(defaults = {}) {
    this.defaults = { ...DEFAULTS, ...defaults };

    // Set up headless WebGL Renderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  /**
   * Render the spread using WebGL
   * @param {HTMLCanvasElement} leftPageCanvas 
   * @param {HTMLCanvasElement} rightPageCanvas 
   * @param {object} options 
   * @returns {HTMLCanvasElement}
   */
  render(leftPageCanvas, rightPageCanvas, options = {}) {
    const opts = { ...this.defaults, ...options };
    
    // Size the renderer
    this.renderer.setSize(opts.width, opts.height);
    this.renderer.setClearColor(0x000000, 0); // Transparent background

    const scene = new THREE.Scene();

    // 1. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 2.5);
    // Position light up and to the top-left
    dirLight.position.set(-5, 10, 5);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 50;
    dirLight.shadow.camera.left = -5;
    dirLight.shadow.camera.right = 5;
    dirLight.shadow.camera.top = 5;
    dirLight.shadow.camera.bottom = -5;
    dirLight.shadow.bias = -0.001;
    scene.add(dirLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight.position.set(5, 5, -5);
    scene.add(fillLight);

    // 2. Shadow Catcher (Invisible Floor)
    const floorGeo = new THREE.PlaneGeometry(30, 30);
    const floorMat = new THREE.ShadowMaterial({ opacity: opts.shadowStrength });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2; // Lay flat
    floor.receiveShadow = true;
    scene.add(floor);

    // 3. Materials
    const leftAspect = leftPageCanvas.height / leftPageCanvas.width;
    const rightAspect = rightPageCanvas.height / rightPageCanvas.width;
    const targetAspect = (opts.aspectRatio && opts.aspectRatio > 0) ? opts.aspectRatio : leftAspect;

    const leftTex = new THREE.CanvasTexture(leftPageCanvas);
    leftTex.colorSpace = THREE.SRGBColorSpace;
    leftTex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this._applyTextureTransform(leftTex, leftAspect, targetAspect, opts);

    const rightTex = new THREE.CanvasTexture(rightPageCanvas);
    rightTex.colorSpace = THREE.SRGBColorSpace;
    rightTex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this._applyTextureTransform(rightTex, rightAspect, targetAspect, opts);

    const matOpts = {
      roughness: opts.roughness,
      metalness: 0.05,
      side: THREE.DoubleSide
    };
    const leftMat = new THREE.MeshStandardMaterial({ ...matOpts, map: leftTex });
    const rightMat = new THREE.MeshStandardMaterial({ ...matOpts, map: rightTex });

    // 4. Physical model — derive spine height, stiffness, and curve shape
    const pageW = 3;
    const pageH = pageW * targetAspect;
    const phys = this._computePhysical(opts.binding, opts.pages, opts.curveIntensity);

    // Paper material for spine block and bottom face
    const paperMat = new THREE.MeshStandardMaterial({
      color: 0xf4f4f4,
      roughness: 0.8,
      metalness: 0.0,
    });

    // Page-edge stripe texture for side faces (Approach C)
    const edgeTex = this._createPageEdgeTexture(phys.pages);
    const edgeMat = new THREE.MeshStandardMaterial({
      map: edgeTex,
      roughness: 0.85,
      metalness: 0.0,
    });

    // 5. Page geometry — thick boxes representing the full page stack.
    //    BoxGeometry(X, Y, Z) = (pageW, stackThickness, pageH)
    //    X = page width (horizontal on table)
    //    Y = stack thickness (vertical, up/down)
    //    Z = page height (horizontal on table, long dimension)
    //    BoxGeometry material order: [right(+X), left(-X), top(+Y), bottom(-Y), front(+Z), back(-Z)]
    //    Top face (+Y, index 2) gets the image — it faces up off the table.
    //    Side faces (0,1,4,5) get the stripe texture to show individual pages.
    const stackT = phys.stackThickness;
    const leftGeo = new THREE.BoxGeometry(pageW, stackT, pageH, 64, 1, 32);
    this._applyPageCurve(leftGeo, pageW, pageH, stackT, phys, false);
    const leftMats = [edgeMat, edgeMat, leftMat, paperMat, edgeMat, edgeMat];

    const rightGeo = new THREE.BoxGeometry(pageW, stackT, pageH, 64, 1, 32);
    this._applyPageCurve(rightGeo, pageW, pageH, stackT, phys, true);
    const rightMats = [edgeMat, edgeMat, rightMat, paperMat, edgeMat, edgeMat];

    // 6. Meshes — offset pages by half the spine width so they emerge from the block
    const spineHalf = phys.spineWidth / 2;
    const leftMesh = new THREE.Mesh(leftGeo, leftMats);
    leftMesh.castShadow = true;
    leftMesh.receiveShadow = true;
    leftMesh.position.set(-pageW / 2 - spineHalf, stackT / 2, 0);

    const rightMesh = new THREE.Mesh(rightGeo, rightMats);
    rightMesh.castShadow = true;
    rightMesh.receiveShadow = true;
    rightMesh.position.set(pageW / 2 + spineHalf, stackT / 2, 0);

    // 7. Spine block — the bound stack of paper between the two pages
    //    Height matches the page stack thickness so they align.
    //    X = spine width, Y = stack thickness (vertical), Z = page height
    let spineMesh = null;
    if (phys.spineWidth > 0.001) {
      const spineGeo = new THREE.BoxGeometry(phys.spineWidth, stackT, pageH);
      spineMesh = new THREE.Mesh(spineGeo, edgeMat);
      spineMesh.position.set(0, stackT / 2, 0);
      spineMesh.castShadow = true;
      spineMesh.receiveShadow = true;
    }

    // Group them to rotate the magazine as a whole
    const magGroup = new THREE.Group();
    magGroup.add(leftMesh);
    magGroup.add(rightMesh);
    if (spineMesh) magGroup.add(spineMesh);
    
    // Apply user rotations
    magGroup.rotation.y = THREE.MathUtils.degToRad(opts.cameraRotateZ); // Spin on table
    scene.add(magGroup);

    // 6. Camera
    const camera = new THREE.PerspectiveCamera(30, opts.width / opts.height, 0.1, 100);
    
    // Auto-frame camera based on bounding box
    const box = new THREE.Box3().setFromObject(magGroup);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const maxDim = Math.max(size.x, size.y, size.z); // Include spine height
    const paddedDim = maxDim * 1.15; // 15% padding
    
    const fov = camera.fov * (Math.PI / 180);
    const radius = Math.abs(paddedDim / (2 * Math.tan(fov / 2)));
    
    const tiltRad = THREE.MathUtils.degToRad(opts.cameraTiltX);
    const rotRad = THREE.MathUtils.degToRad(opts.cameraRotateY);
    
    camera.position.y = radius * Math.cos(tiltRad); // Height
    camera.position.z = radius * Math.sin(tiltRad) * Math.cos(rotRad);
    camera.position.x = radius * Math.sin(tiltRad) * Math.sin(rotRad);
    
    camera.lookAt(center);

    // Render!
    this.renderer.render(scene, camera);

    // Create a detached canvas to return (so WebGLRenderer can be reused safely)
    const outCanvas = document.createElement('canvas');
    outCanvas.width = opts.width;
    outCanvas.height = opts.height;
    const outCtx = outCanvas.getContext('2d');
    outCtx.drawImage(this.renderer.domElement, 0, 0);
    
    // Cleanup Three.js resources to prevent memory leaks
    leftTex.dispose();
    rightTex.dispose();
    leftMat.dispose();
    rightMat.dispose();
    paperMat.dispose();
    edgeMat.dispose();
    edgeTex.dispose();
    leftGeo.dispose();
    rightGeo.dispose();
    if (spineMesh) spineMesh.geometry.dispose();
    floorGeo.dispose();
    floorMat.dispose();
    ambientLight.dispose();
    dirLight.dispose();
    fillLight.dispose();
    
    return outCanvas;
  }

  /**
   * Compute physical parameters from binding type and page count.
   *
   * @param {'saddle'|'perfect'} binding
   * @param {number} pages
   * @param {number} edgeLift  User-controlled outer edge lift (Curl slider)
   * @returns {{spineHeight: number, spineWidth: number, spineDrop: number, stiffness: number, edgeLift: number}}
   */
  _computePhysical(binding, pages, edgeLift) {
    const p = Math.max(4, Math.min(400, Number(pages) || 60));
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

    if (binding === 'perfect') {
      const spineHeight = clamp(p * SHEET, 0.02, 0.6);
      return {
        spineHeight,
        spineWidth: spineHeight,           // Square spine block
        // Visual stack thickness — scaled up from physical so the page
        // block reads as a thick magazine, not a single sheet.
        stackThickness: clamp(0.06 + p * 0.001, 0.06, 0.4),
        // Visual curve amplitude — much larger than physical spine height
        // so the curl is visible from any camera angle.
        curveAmplitude: clamp(0.15 + p * 0.001, 0.15, 0.45),
        edgeLift: clamp(edgeLift, 0, 0.5),
        pages: p,
      };
    }
    // Saddle stitch — folded crease, thin, floppy
    const spineHeight = clamp(p * SHEET * 0.25, 0.005, 0.08);
    return {
      spineHeight,
      spineWidth: clamp(p * SHEET * 0.15, 0.003, 0.04), // Thin fold
      // Saddle stitch is thinner — folded pages, not a glued stack
      stackThickness: clamp(0.03 + p * 0.0005, 0.03, 0.12),
      // Saddle stitch lies much flatter — small curve amplitude
      curveAmplitude: clamp(0.05 + p * 0.0005, 0.05, 0.18),
      edgeLift: clamp(edgeLift, 0, 0.5),
      pages: p,
    };
  }

  /**
   * Create a canvas texture showing horizontal page-edge stripes.
   * Applied to the side faces of the page box to suggest a stack of
   * individual pages rather than a solid block.
   *
   * @param {number} pages  Page count — more pages = tighter stripes
   * @returns {THREE.CanvasTexture}
   */
  _createPageEdgeTexture(pages) {
    const canvas = document.createElement('canvas');
    canvas.width = 4;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // Base paper color
    ctx.fillStyle = '#f0f0f0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw horizontal lines representing individual page edges.
    // More pages = more lines = tighter spacing.
    const lineCount = Math.min(pages, 200);
    const spacing = canvas.height / lineCount;
    ctx.fillStyle = '#d8d8d8';
    for (let i = 0; i < lineCount; i++) {
      const y = Math.round(i * spacing);
      ctx.fillRect(0, y, canvas.width, 1);
    }

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    return tex;
  }

  /**
   * Cubic Bezier interpolation.
   * @param {number} t  0..1
   * @param {number} p0
   * @param {number} p1
   * @param {number} p2
   * @param {number} p3
   * @returns {number}
   */
  _cubicBezier(t, p0, p1, p2, p3) {
    const mt = 1 - t;
    return (
      p0 * mt * mt * mt +
      p1 * 3 * mt * mt * t +
      p2 * 3 * mt * t * t +
      p3 * t * t * t
    );
  }

  /**
   * Mutates a BoxGeometry to look like a curved magazine page.
   *
   * Geometry is in world orientation: X=page width, Y=thickness, Z=page height.
   * The curve lifts vertices in +Y (up off the table) using a cubic Bezier
   * cantilever profile: high at the spine, settling to edgeLift at the outer edge.
   *
   * @param {THREE.BoxGeometry} geometry
   * @param {number} pageW  Page width (X)
   * @param {number} pageH  Page height (Z)
   * @param {number} thickness  Box thickness (Y)
   * @param {object} phys  Physical params from _computePhysical
   * @param {boolean} isRightPage
   */
  _applyPageCurve(geometry, pageW, pageH, thickness, phys, isRightPage) {
    const pos = geometry.attributes.position;
    const halfThick = thickness / 2;

    // Bezier control points for the page surface height profile (Y axis)
    // t=0 (outer edge) → t=1 (spine)
    // Shape: flat at edge, flat until ~2/3, gentle rise to peak, sharp drop at spine
    // Curl slider controls the peak height (P2) — how much pages rise from the spine.
    const P0 = 0;                                      // Edge: always flat on table
    const P1 = 0;                                      // Flat through middle
    const P2 = phys.curveAmplitude + phys.edgeLift;   // Near spine: baseline + Curl slider
    const P3 = 0;                                      // Spine: drops into binding

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);

      // Normalise X from 0 to 1 across the page width
      const nx = (x + pageW / 2) / pageW;
      // distFromEdge: 0 at outer edge, 1 at spine (matches Bezier t parameter)
      const distFromEdge = isRightPage ? (1 - nx) : nx;

      // Normalise Z from 0 to 1 across the page height
      const nz = (z + pageH / 2) / pageH;
      // Vertical variation: curl is more pronounced at top/bottom edges,
      // gentler in the middle where the binding holds tighter.
      const heightFactor = 1 + 0.15 * Math.abs(nz - 0.5) * 2;

      // Base cantilever curve (Y = up, off the table)
      const baseY = this._cubicBezier(distFromEdge, P0, P1, P2, P3);
      const curveY = baseY * heightFactor;

      // Offset the Y position: top surface goes up, bottom surface goes down
      const ySign = y > 0 ? 1 : -1;
      pos.setY(i, curveY + ySign * halfThick);
    }
    geometry.computeVertexNormals();
  }

  /**
   * Apply a UV transform to a page texture so the source image is cover-fit
   * (center-cropped) into the target page aspect, then zoomed/panned.
   *
   * @param {THREE.Texture} tex
   * @param {number} imageAspect Source image height/width
   * @param {number} targetAspect Target page height/width
   * @param {object} opts { zoom, panX, panY }
   */
  _applyTextureTransform(tex, imageAspect, targetAspect, opts) {
    const zoom = opts.zoom && opts.zoom > 0 ? opts.zoom : 1;
    let repeatX = 1;
    let repeatY = 1;
    if (imageAspect > targetAspect) {
      repeatY = targetAspect / imageAspect;
    } else {
      repeatX = imageAspect / targetAspect;
    }
    repeatX /= zoom;
    repeatY /= zoom;

    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.repeat.set(repeatX, repeatY);

    const slackX = 1 - repeatX;
    const slackY = 1 - repeatY;
    const panX = opts.panX ?? 0;
    const panY = opts.panY ?? 0;
    tex.offset.set(
      slackX * (0.5 + 0.5 * panX),
      slackY * (0.5 - 0.5 * panY),
    );
    tex.needsUpdate = true;
  }

  /**
   * Dispose the WebGL renderer and free GPU resources.
   * Call when the renderer is no longer needed (e.g. app teardown).
   */
  dispose() {
    this.renderer.dispose();
  }
}
